import { useTranslation } from 'react-i18next';
import { TicketIcon } from '@phosphor-icons/react/dist/csr/Ticket';

import { Card } from '../../design-system/composites/Card/Card';
import type { CodeAvailabilityRow } from '../../lib/api/codes';
import styles from './CodeAvailability.module.css';

/**
 * The free-count summary — `GET /dispensary-codes/availability`, rendered
 * exactly as `codesApi.availability()` hands it back: one row per
 * `(dispensary, kind, type_code)`, a FREE count only (`codes.ts`'s own
 * docblock — a bound code is history, not stock on hand).
 *
 * A dumb display component on purpose: `CodeScreen` owns the fetch (and the
 * refetch after an upload or a create), so this file has no `useEffect` and
 * no opinion about when its own numbers go stale.
 */

export type CodeAvailabilityStatus = 'loading' | 'ready' | 'error';

export interface CodeAvailabilityProps {
  rows: readonly CodeAvailabilityRow[];
  status: CodeAvailabilityStatus;
}

function rowKey(row: CodeAvailabilityRow): string {
  return `${row.dispensary.uuid ?? 'null'}-${row.kind}-${row.type_code}`;
}

export function CodeAvailability({ rows, status }: CodeAvailabilityProps) {
  const { t, i18n } = useTranslation();

  return (
    // One card holding the whole summary, so its title, its status line and
    // its counts read on the card ground rather than over the background.
    <Card wash icon={<TicketIcon size={16} />} title={t('codes.availabilityTitle')}>

      {/* A polite region, mounted in every state — the same reasoning
          DataTable's own `role="status"` region carries: an empty message on
          the first frame, changed text (never a fresh element) when the
          count actually moves. */}
      <p role="status" className={styles['note']}>
        {status === 'loading' ? t('codes.availabilityLoading') : ''}
        {status === 'error' ? t('codes.availabilityFailed') : ''}
        {status === 'ready' && rows.length === 0 ? t('codes.availabilityEmpty') : ''}
      </p>

      {status === 'ready' && rows.length > 0 ? (
        <ul className={styles['list']}>
          {rows.map((row) => (
            <li key={rowKey(row)} className={styles['row']}>
              <span className={styles['label']}>
                {/*
                  BOTH names are individually nullable here, unlike every
                  other dispensary ref — `availability()` groups by
                  `dispensary_id` and a since-soft-deleted dispensary drops
                  out of the lookup. So this picks the locale's name, falls
                  back to the other one, and only then to the placeholder,
                  rather than showing "Unknown" for a dispensary whose name
                  the response actually carries in the other language.
                */}
                {(i18n.language.startsWith('ar')
                  ? (row.dispensary.name_ar ?? row.dispensary.name_en)
                  : (row.dispensary.name_en ?? row.dispensary.name_ar)) ??
                  t('codes.unknownDispensary')}{' '}
                ·{' '}
                {t(`codes.kind.${row.kind}`)} · {t(`codes.type.${row.type_code}`)}
              </span>
              <bdi dir="ltr" className={styles['count']} data-testid="codeFreeCount">
                {row.free_count}
              </bdi>
            </li>
          ))}
        </ul>
      ) : null}
    </Card>
  );
}
